import { createSlice, type PayloadAction } from '@spcc/core'
import type {
  DateRange,
  SalesDashboardFilters,
  SubCustomerAudience,
  SubCustomerDashboardState,
  SubCustomerDetail,
  SubCustomerOptions,
  SubCustomerRef,
  SubCustomerTop,
} from '../domain/types'
import { DEFAULT_DATE_PRESET, SUB_CUSTOMER_SEARCH_MIN_CHARS } from '../domain/constants'

const EMPTY_OPTIONS: SubCustomerOptions = { customers: [], cities: [], cementTypes: [] }

export const initialSubCustomerDashboardState: SubCustomerDashboardState = {
  dateRange: { preset: DEFAULT_DATE_PRESET, from: null, to: null },
  filters: {},
  options: EMPTY_OPTIONS,
  optionsKey: null,
  top: null,
  search: { query: '', results: [], loading: false },
  selected: null,
  detail: null,
  exportDialog: { open: false, target: null },
  loading: { top: false, detail: false, export: false },
  error: null,
}

function startRefresh(state: SubCustomerDashboardState) {
  state.loading.top = true
  state.error = null
}

export interface ExportRequestedPayload {
  from: string
  to: string
  audience: SubCustomerAudience
  /** Export only this sub-customer's orders. */
  subCustomerId?: string
}

export const subCustomerDashboardSlice = createSlice({
  name: 'subCustomerDashboard',
  initialState: initialSubCustomerDashboardState,
  reducers: {
    /** Page mount / header scope change: start over with the current controls. */
    init: (state) => {
      state.selected = null
      state.detail = null
      state.search = { query: '', results: [], loading: false }
      startRefresh(state)
    },
    refresh: startRefresh,
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload
      startRefresh(state)
    },
    setFilters: (state, action: PayloadAction<SalesDashboardFilters>) => {
      state.filters = action.payload
      startRefresh(state)
    },
    fetchOptionsSuccess: (state, action: PayloadAction<{ key: string; options: SubCustomerOptions }>) => {
      state.options = action.payload.options
      state.optionsKey = action.payload.key
    },
    /** Forget the key so the next refresh retries. */
    fetchOptionsFailure: (state) => {
      state.optionsKey = null
    },
    fetchTopSuccess: (state, action: PayloadAction<SubCustomerTop>) => {
      state.top = action.payload
      state.loading.top = false
    },
    fetchTopFailure: (state, action: PayloadAction<string>) => {
      state.loading.top = false
      state.error = action.payload
    },

    searchQueryChanged: (state, action: PayloadAction<string>) => {
      const query = action.payload
      const searchable = query.trim().length >= SUB_CUSTOMER_SEARCH_MIN_CHARS
      state.search = { query, results: searchable ? state.search.results : [], loading: searchable }
    },
    searchSuccess: (state, action: PayloadAction<SubCustomerRef[]>) => {
      state.search.results = action.payload
      state.search.loading = false
    },
    searchFailure: (state) => {
      state.search.results = []
      state.search.loading = false
    },

    selectSubCustomer: (state, action: PayloadAction<SubCustomerRef | null>) => {
      state.selected = action.payload
      state.detail = null
      state.loading.detail = action.payload !== null
    },
    fetchDetail: (state) => {
      state.loading.detail = state.selected !== null
    },
    fetchDetailSuccess: (state, action: PayloadAction<{ id: string; detail: SubCustomerDetail }>) => {
      // A late response for a sub-customer the user already moved away from is dropped.
      if (state.selected?.id !== action.payload.id) return
      state.detail = action.payload.detail
      state.loading.detail = false
    },
    fetchDetailFailure: (state) => {
      state.loading.detail = false
    },

    openExport: (state, action: PayloadAction<SubCustomerRef | null>) => {
      state.exportDialog = { open: true, target: action.payload }
    },
    closeExport: (state) => {
      state.exportDialog.open = false
    },
    exportRequested: (state, _action: PayloadAction<ExportRequestedPayload>) => {
      state.loading.export = true
    },
    exportFinished: (state, action: PayloadAction<{ success: boolean }>) => {
      state.loading.export = false
      if (action.payload.success) state.exportDialog.open = false
    },
  },
})

export const subCustomerDashboardActions = subCustomerDashboardSlice.actions
export const subCustomerDashboardReducer = subCustomerDashboardSlice.reducer
